import React from 'react';
import { X, Sparkles, ExternalLink } from 'lucide-react';
import { usePerfil } from '../context/PerfilContext';
import { usePlus } from '../context/PlusContext';
import { useCerrarConEscape } from '../hooks/useCerrarConEscape';
import { abrirEnlaceExterno, esApp } from '../utils/plataforma';

/**
 * Cómo configurar la automatización de Wallet para que, al pagar con Apple Pay,
 * se abra myarinn con el gasto (acción «Apuntar pago de Apple Pay» de
 * ios/App/App/AtajosMyarinn.swift). iOS no deja a ninguna app crear la
 * automatización: la crea la persona en Atajos, una vez. Es de Plus.
 *
 * Se abre con /finanzas/dashboard?applepay=1 (Ayuda, asistente, novedad).
 */
const PASOS = [
  'Abre la app Atajos y toca «Automatización», abajo.',
  'Toca «+» o «Nueva automatización» y elige «Transacción» (es de Wallet).',
  'Marca las tarjetas con las que pagas y elige «Ejecutar inmediatamente». Toca «Siguiente».',
  'Toca «Nueva automatización en blanco», luego «Añadir acción», busca myarinn y elige «Apuntar pago de Apple Pay».',
  'En la acción, toca «Importe» y elige la variable «Importe» de la transacción. Haz lo mismo con «Comercio» y con «Tarjeta». Toca «OK» y listo.',
];

export const GuiaApplePay: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  useCerrarConEscape(onClose);
  const { esPlus } = usePerfil();
  const { ofrecerPlus } = usePlus();

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center capa-hoja" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} role="dialog" aria-labelledby="titulo-applepay"
        className="hoja bg-white dark:bg-gray-800 w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl shadow-xl max-h-full overflow-y-auto">
        <div className="flex items-start justify-between gap-3 px-5 pt-5">
          <div>
            <p className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wide text-amber-600 dark:text-amber-400">
              <Sparkles className="w-3.5 h-3.5" /> Plus
            </p>
            <h2 id="titulo-applepay" className="text-lg font-bold text-gray-900 dark:text-white mt-0.5">
              Apunta al pagar con Apple Pay
            </h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Cerrar" className="boton-cerrar text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 pb-5 pt-2 space-y-4 text-sm text-gray-700 dark:text-gray-300">
          <p>
            Cada vez que pagues con Apple Pay en una tienda, myarinn se abrirá con el importe y la tienda
            puestos, y con la categoría de la última vez. Solo tienes que revisar y guardar.
          </p>
          <p className="text-gray-500 dark:text-gray-400">
            Se configura una sola vez en la app Atajos del iPhone (Apple no deja que ninguna app lo haga sola).
            Tardas un minuto:
          </p>
          <ol className="space-y-2.5">
            {PASOS.map((p, i) => (
              <li key={i} className="flex gap-3">
                <span className="w-6 h-6 shrink-0 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center">
                  {i + 1}
                </span>
                <span>{p}</span>
              </li>
            ))}
          </ol>
          <p className="text-gray-500 dark:text-gray-400">
            La primera vez que pagues con cada tarjeta, myarinn te preguntará en qué cuenta va y ya lo recordará.
            Solo funciona con Apple Pay en tiendas, no con la tarjeta física.
          </p>

          {!esPlus ? (
            <button type="button" onClick={() => { onClose(); ofrecerPlus('applePay'); }}
              className="w-full min-h-[44px] rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-semibold transition">
              Ver myarinn Plus
            </button>
          ) : esApp ? (
            <button type="button" onClick={() => abrirEnlaceExterno('shortcuts://').catch(() => undefined)}
              className="w-full min-h-[44px] inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold transition">
              <ExternalLink className="w-4 h-4" /> Abrir Atajos
            </button>
          ) : (
            <p className="rounded-lg bg-gray-50 dark:bg-gray-700/50 px-3 py-2 text-gray-600 dark:text-gray-300">
              Hazlo en el iPhone, con la app de myarinn instalada y actualizada.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
